import React, { useState, useCallback } from "react";

import Button from "../../components/design-system/Button";
import Checkbox from "../../components/design-system/Checkbox";
import { cn } from "@faire/prototype-playground/lib/utils";

interface Category {
  id: string;
  label: string;
  description: string;
  children?: Category[];
}

const CATEGORIES: Category[] = [
  {
    id: "bath-body",
    label: "Bath & body",
    description: "Soaps, lotions, bath bombs",
    children: [
      { id: "bath-body-soaps", label: "Soaps", description: "" },
      { id: "bath-body-lotions", label: "Lotions", description: "" },
      { id: "bath-body-bath-bombs", label: "Bath bombs", description: "" },
    ],
  },
  {
    id: "books",
    label: "Books",
    description: "Fiction, non-fiction, children's",
    children: [
      { id: "books-fiction", label: "Fiction", description: "" },
      { id: "books-nonfiction", label: "Non-fiction", description: "" },
      { id: "books-children", label: "Children's", description: "" },
    ],
  },
  {
    id: "fitness-yoga",
    label: "Fitness & yoga",
    description: "Mats, accessories, apparel",
    children: [
      { id: "fitness-mats", label: "Mats", description: "" },
      { id: "fitness-accessories", label: "Accessories", description: "" },
      { id: "fitness-apparel", label: "Apparel", description: "" },
    ],
  },
  {
    id: "food-drink",
    label: "Food & drink",
    description: "Snacks, beverages, chocolate",
    children: [
      { id: "food-snacks", label: "Snacks", description: "" },
      { id: "food-beverages", label: "Beverages", description: "" },
      { id: "food-chocolate", label: "Chocolate", description: "" },
    ],
  },
  {
    id: "fragrance",
    label: "Fragrance",
    description: "Perfume, candles, incense",
    children: [
      { id: "fragrance-perfume", label: "Perfume", description: "" },
      { id: "fragrance-candles", label: "Candles", description: "" },
      { id: "fragrance-incense", label: "Incense", description: "" },
    ],
  },
  {
    id: "haircare",
    label: "Haircare",
    description: "Shampoo, conditioner, styling",
    children: [
      { id: "haircare-shampoo", label: "Shampoo", description: "" },
      { id: "haircare-conditioner", label: "Conditioner", description: "" },
      { id: "haircare-styling", label: "Styling", description: "" },
    ],
  },
  {
    id: "jewelry",
    label: "Jewelry",
    description: "Necklaces, earrings, bracelets",
    children: [
      { id: "jewelry-necklaces", label: "Necklaces", description: "" },
      { id: "jewelry-earrings", label: "Earrings", description: "" },
      { id: "jewelry-bracelets", label: "Bracelets", description: "" },
    ],
  },
  {
    id: "kids-baby",
    label: "Kids & baby",
    description: "Toys, clothing, accessories",
    children: [
      { id: "kids-toys", label: "Toys", description: "" },
      { id: "kids-clothing", label: "Clothing", description: "" },
      { id: "kids-accessories", label: "Accessories", description: "" },
    ],
  },
  {
    id: "makeup",
    label: "Makeup",
    description: "Eyes, face, lips",
    children: [
      { id: "makeup-eyes", label: "Eyes", description: "" },
      { id: "makeup-face", label: "Face", description: "" },
      { id: "makeup-lips", label: "Lips", description: "" },
    ],
  },
  {
    id: "mens-apparel",
    label: "Men's apparel",
    description: "Shirts, pants, outerwear",
    children: [
      { id: "mens-shirts", label: "Shirts", description: "" },
      { id: "mens-pants", label: "Pants", description: "" },
      { id: "mens-outerwear", label: "Outerwear", description: "" },
    ],
  },
  {
    id: "personal-care",
    label: "Personal care",
    description: "Oral care, deodorant, shaving",
    children: [
      { id: "personal-oral", label: "Oral care", description: "" },
      { id: "personal-deodorant", label: "Deodorant", description: "" },
      { id: "personal-shaving", label: "Shaving", description: "" },
    ],
  },
  {
    id: "skincare",
    label: "Skincare",
    description: "Moisturizers, serums, cleansers",
    children: [
      { id: "skincare-moisturizers", label: "Moisturizers", description: "" },
      { id: "skincare-serums", label: "Serums", description: "" },
      { id: "skincare-cleansers", label: "Cleansers", description: "" },
    ],
  },
  {
    id: "pets",
    label: "Pets",
    description: "Treats, toys, grooming",
    children: [
      { id: "pets-treats", label: "Treats", description: "" },
      { id: "pets-toys", label: "Toys", description: "" },
      { id: "pets-grooming", label: "Grooming", description: "" },
    ],
  },
  {
    id: "womens-apparel",
    label: "Women's apparel",
    description: "Tops, dresses, bottoms",
    children: [
      { id: "womens-tops", label: "Tops", description: "" },
      { id: "womens-dresses", label: "Dresses", description: "" },
      { id: "womens-bottoms", label: "Bottoms", description: "" },
    ],
  },
];

const MAX_SELECTIONS = 10;

export default function Variant2() {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [selectedSubs, setSelectedSubs] = useState<Set<string>>(new Set());

  const totalSelections = selectedSubs.size;
  const atLimit = totalSelections >= MAX_SELECTIONS;

  const toggleExpanded = useCallback((catId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  }, []);

  const toggleSubcategory = useCallback(
    (subId: string) => {
      setSelectedSubs((prev) => {
        const next = new Set(prev);
        if (next.has(subId)) {
          next.delete(subId);
        } else if (!atLimit) {
          next.add(subId);
        }
        return next;
      });
    },
    [atLimit]
  );

  return (
    <div className="flex min-h-screen items-start justify-center bg-[#f5f5f5] py-8">
      <div
        className="flex flex-col overflow-hidden rounded-3xl bg-fs-surface-primary shadow-lg"
        style={{ width: 375, height: 812 }}
      >
        {/* Nav bar */}
        <div className="relative flex shrink-0 items-center justify-center px-6 py-4">
          <button
            type="button"
            className="absolute left-2 p-3.5 text-[#333]"
            aria-label="Back"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3L5 8L10 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span
            className="text-base tracking-[0.35em] text-[#333]"
            style={{ fontFamily: "'Graphik', sans-serif", fontWeight: 400 }}
          >
            F A I R E
          </span>
        </div>

        {/* Progress bar */}
        <div className="flex w-full shrink-0">
          <div className="h-1 w-1/4 bg-[#333]" />
          <div className="h-1 flex-1 bg-[#f7f7f7]" />
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 pt-8">
          <h1
            className="mb-4 text-[30px] leading-[38px] text-[#333]"
            style={{ fontFamily: "'Nantes', serif" }}
          >
            What product categories are you interested in?
          </h1>
          <p
            className="mb-6 text-sm leading-5 tracking-[0.15px] text-[#333]"
            style={{ fontFamily: "'Graphik', sans-serif" }}
          >
            Tap a category to explore subcategories. Pick up to{" "}
            {MAX_SELECTIONS}.
          </p>

          {/* Selection counter */}
          {totalSelections > 0 && (
            <div
              className="mb-4 text-xs tracking-[0.15px] text-[#757575]"
              style={{ fontFamily: "'Graphik', sans-serif" }}
            >
              {totalSelections} of {MAX_SELECTIONS} selected
            </div>
          )}

          {/* Toggle list */}
          <div className="mb-6 overflow-hidden rounded-lg border border-[#dfe0e1]">
            {CATEGORIES.map((cat, idx) => {
              const isExpanded = expandedCategories.has(cat.id);
              const subCount = cat.children
                ? cat.children.filter((c) => selectedSubs.has(c.id)).length
                : 0;

              return (
                <div
                  key={cat.id}
                  className={
                    idx < CATEGORIES.length - 1
                      ? "border-b border-[#f0f0f0]"
                      : ""
                  }
                >
                  <button
                    type="button"
                    onClick={() => toggleExpanded(cat.id)}
                    className={cn("flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150", isExpanded ? "bg-[#f7f7f7]" : "bg-fs-surface-primary hover:bg-[#fafafa]")}
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-sm font-medium text-[#333]"
                        style={{
                          fontFamily: "'Graphik', sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        {cat.label}
                      </div>
                      <div
                        className="mt-0.5 text-xs text-[#757575]"
                        style={{ fontFamily: "'Graphik', sans-serif" }}
                      >
                        {cat.description}
                      </div>
                    </div>

                    {subCount > 0 && (
                      <span
                        className="shrink-0 rounded-full bg-[#333] px-1.5 py-0.5 text-[10px] font-medium text-fs-text-primary-inverse"
                        style={{ fontFamily: "'Graphik', sans-serif" }}
                      >
                        {subCount}
                      </span>
                    )}

                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="shrink-0 text-[#999] transition-transform duration-200"
                      style={{
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    >
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <div
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      maxHeight: isExpanded
                        ? `${(cat.children?.length || 0) * 40 + 24}px`
                        : "0px",
                      opacity: isExpanded ? 1 : 0,
                    }}
                  >
                    <div className="border-t border-[#f0f0f0] bg-[#fafafa] px-4 py-3">
                      <div className="flex flex-col gap-2.5">
                        {cat.children?.map((child) => (
                          <Checkbox
                            key={child.id}
                            label={child.label}
                            checked={selectedSubs.has(child.id)}
                            onChange={() => toggleSubcategory(child.id)}
                            disabled={
                              atLimit && !selectedSubs.has(child.id)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sticky bottom button */}
        <div className="shrink-0 border-t border-[#f0f0f0] bg-fs-surface-primary px-6 py-4">
          <Button
            variant="primary"
            disabled={totalSelections === 0}
            className="w-full"
            onClick={() => {
              const subs = Array.from(selectedSubs);
              alert(`Selected subcategories:\n${subs.join("\n")}`);
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
